import { useState, MouseEvent, useCallback, useMemo, useEffect } from 'react';

import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import debounce from 'lodash/debounce';
import { observer } from 'mobx-react-lite';

import { Asset } from '../../../../models/assets/asset';
import { DEBOUNCE_DURATION } from '../../../../utils/constants';
import { useAssetsStore } from '../../AssetsProvider';
import { fileStore } from '../AddAttachments';

import CountItem from './CountItem';
import FileList from './List';

interface AttachmentListProps {
  asset: Asset;
}

export default observer(({ asset }: AttachmentListProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const {
    loadAttachments,
    chosenAsset,
    uploadAssets,
    fetchAssets,
    removeChosenAsset,
    startGallery,
    closeGallery,
    directCallCarousel,
    gallery,
  } = useAssetsStore();

  useEffect(() => {
    // Close attachments menu when carousel is opened, or when gallery is closed (chosenAsset removed)
    if (gallery?.showCarousel) {
      setAnchorEl(null);
    }
    if (chosenAsset === undefined)  {
       setAnchorEl(null);
    }
  }, [anchorEl, chosenAsset, gallery?.showCarousel, setAnchorEl])

  const attachmentsCount = useMemo(
    () =>
      chosenAsset?.id === asset.id
        ? chosenAsset?.attachments.totalAttachments
        : asset.totalAttachments,
    [
      asset.id,
      chosenAsset?.id,
      asset.totalAttachments,
      chosenAsset?.attachments.totalAttachments,
    ],
  );

  const handlePreview = (initFileId: number) => {
    return startGallery(initFileId, m => m.gallery.togglePreview());
  };

  const debouncedCarousel = useMemo(
    () =>
      debounce(() => {
        if (asset?.id !== chosenAsset?.id && attachmentsCount > 0) {
          return directCallCarousel(asset?.id, m => m.gallery.togglePreview());
        }
      }, DEBOUNCE_DURATION),
    [asset?.id, chosenAsset?.id, directCallCarousel, attachmentsCount],
  );

  const handleOpenCarousel = useCallback(
    () => debouncedCarousel(),
    [debouncedCarousel],
  );

  const openUploadModal = (id: number) => {
    uploadAssets(id);
    if (anchorEl) {
      setAnchorEl(null);
    }
    fileStore.fileDialogStore.openDialog();
  };

  const handleAttachment = (event: MouseEvent<HTMLElement>) => {
    (async () => {
      try {
        if (asset?.id !== chosenAsset?.id) {
          await loadAttachments(asset?.id);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    })()
    setAnchorEl(event.currentTarget)
  };

  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
    removeChosenAsset();
    closeGallery();
  };

  const onDeletion = () => {
    if (!chosenAsset?.attachments.totalAttachments) setAnchorEl(null);
    fetchAssets();
  };
  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Box
    sx={{
      mx: '-8px', //best way for styling Attachment cell
      width: 'calc(100% + 16px)',
      flex: 1
    }}
      onDoubleClick={stopPropagation}>
      <CountItem
        collapsed={open}
        asset={asset}
        uploadHandler={id => openUploadModal(id)}
        handleOpenList={handleAttachment}
        handleToggleCarousel={handleOpenCarousel}
      />
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        marginThreshold={8}
        PaperProps={{
          sx: {
            height: 'min(310px, 40vh)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
          disablePadding: true,
          sx: {
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          },
        }}
      >
        <FileList
          asset={chosenAsset}
          handlePreview={handlePreview}
          uploadHandler={id => openUploadModal(id)}
          onClose={handleClose}
          chosenAsset={chosenAsset}
          onDeletion={onDeletion}
        />
      </Menu>
    </Box>
  );
});
